import React from 'react';
import UserProfileEntry from '~store/Profile/UserProfileEntry';
import { useQuery } from '@tanstack/react-query';
import { Profile, ZoneEntry } from '@ixicare/resources';
import { t } from '~i18n';
import { useZones } from '~hooks/zone';
import styles from './ResidentConfigDetail.module.css';
import { Spinner } from '@ixicare/ui';

export interface ResidentConfigDetailProps {
  profile: UserProfileEntry;
}

export function useProfileConfiguration(profileId: number) {
  const query = useQuery({
    queryKey: ['profileConfiguration', profileId],
    queryFn: () => Profile.getProfileConfiguration(profileId),
    retry: false,
    refetchOnWindowFocus: false,
  });
  return {
    isLoading: query.isLoading,
    data: query.data,
  };
}

export const ResidentConfigDetail: React.FC<ResidentConfigDetailProps> = (props) => {
  const query = useQuery({
    queryKey: ['profileConfiguration', props.profile.id],
    queryFn: () => Profile.getProfileConfiguration(props.profile.id),
    retry: false,
    refetchOnWindowFocus: false,
  });
  const siteUuid = props.profile.site?.uuid;
  const allZones = useZones({ siteUuid });
  let indoorGeofenceZones: ZoneEntry[] = [];

  if (query.data && query.data?.['indoor-geofencing-zones']?.length != 0) {
    const zones = query.data['indoor-geofencing-zones'];
    if (zones && zones?.length != 0) {
      const mappedZones = zones.map((uuid) => allZones.find((z) => z.uuid === uuid)).filter((z): z is ZoneEntry => !!z);
      indoorGeofenceZones = mappedZones;
    }
  }

  if (query.isLoading) {
    return <Spinner />;
  }

  console.log(query.data?.['outdoor-geofencing-mode']);

  return (
    <div>
      <div className="row">
        {props.profile.ownedDevice && (
          <div className="col-12 col-md-6">
            <p className={styles.heading}>Ixicare Device</p>
            <div className={styles.block}>
              <p className={styles.label}>{t('devices.table.macAddress')}</p>
              <p className={styles.value}>{props.profile.ownedDevice?.macAddress}</p>
            </div>
            <div className={styles.block}>
              <p className={styles.label}>{t('devices.table.deviceType')} </p>
              <p className={styles.value}>
                {t(`device.types.${props.profile.ownedDevice?.deviceType?.toLowerCase()}`)}
              </p>
            </div>
            {props.profile.ownedDevice.phoneNumber && (
              <div className={styles.block}>
                <p className={styles.label}>{t('alert.call.devicePhoneNumber')}</p>
                <p className={styles.value}>{props.profile.ownedDevice.phoneNumber}</p>
              </div>
            )}
          </div>
        )}
        {!query.isLoading && (
          <div className="col-12 col-md-6">
            <p className={styles.heading}>Ixicare Device Settings</p>
            <div className={styles.block}>
              <p className={styles.label}>{t('profile-configuration.indoor-checkin-frequency.label')}</p>
              <p className={styles.value}>{query.data?.['indoor-checkin-frequency']}s</p>
            </div>
            <div className={styles.block}>
              <p className={styles.label}>{t('profile-configuration.indoor-geofencing-enabled.label')}</p>
              <p className={styles.value}>{query.data?.['indoor-geofencing-enabled'] ? 'Enabled' : 'Disabled'}</p>
              {query.data?.['indoor-geofencing-enabled'] && indoorGeofenceZones && (
                <div>
                  <p className={styles.label}>{t('profile.configuration.indoor-geofencing-allowed-zones')}</p>
                  {indoorGeofenceZones.map((zone) => (
                    <p className={styles.value}>{zone.name}</p>
                  ))}
                </div>
              )}
            </div>
            <div className={styles.block}>
              <p className={styles.label}>{t('profile-configuration.outdoor-geofencing-mode.label')}</p>
              <p className={styles.value}>
                {query.data?.['outdoor-geofencing-mode'] != 'none'
                  ? `Enabled - ${query.data?.['outdoor-geofencing-mode']}`
                  : 'Disabled'}
              </p>
              {query.data?.['outdoor-geofencing-mode'] === 'geo' && (
                <p className={styles.disclaimer}>{t('profile-configuration.outdoor-geofencing-geo.disclaimer')}</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
